// src/pages/healper/decryption.tsx
import sodium from 'libsodium-wrappers';
import { Buffer } from 'buffer';
import React from 'react';

let sodiumReady = false;
const decryptionCache = new Map();

// Initialize sodium
(async () => {
  await sodium.ready;
  sodiumReady = true;
})();

// Helper: Check if message *looks* like a valid encrypted payload
const BASE64_REGEX = /^[A-Za-z0-9+/=]+$/;

const looksEncrypted = (message: string) => {
  if (typeof message !== 'string' || !message) return false;
  if (!BASE64_REGEX.test(message)) return false;

  try {
    const buf = Buffer.from(message, 'base64');
    return buf.length > sodium.crypto_secretbox_NONCEBYTES;
  } catch {
    return false;
  }
};

// Async version
const decryptTextAsync = async (message: string) => {
  try {
    if (typeof message !== 'string' || !message) {
      return 'N/A';
    }

    await sodium.ready;

    if (!looksEncrypted(message)) {
      return message; // Not encrypted, return as-is
    }

    const secretKey = sodium.from_hex(import.meta.env.VITE_APP_SECRET);
    const encryptedMessage = Buffer.from(message, 'base64');
    const nonce = encryptedMessage.slice(0, sodium.crypto_secretbox_NONCEBYTES);
    const ciphertext = encryptedMessage.slice(
      sodium.crypto_secretbox_NONCEBYTES
    );

    const decryptedMessage = sodium.crypto_secretbox_open_easy(
      ciphertext,
      nonce,
      secretKey
    );

    return decryptedMessage ? sodium.to_string(decryptedMessage) : 'N/A';
  } catch (e) {
    console.error('Decryption error:', e, 'for message:', message);
    return 'N/A';
  }
};

// Non-async wrapper
export const decryptText = (message: string) => {
  if (decryptionCache.has(message)) {
    return decryptionCache.get(message);
  }

  if (typeof message !== 'string' || !message) {
    return 'N/A';
  }

  if (!looksEncrypted(message)) {
    return message; // Not encrypted, return as-is
  }

  if (!sodiumReady) {
    decryptTextAsync(message).then((decrypted) => {
      decryptionCache.set(message, decrypted);
      triggerRerender();
    });
    return 'Loading...';
  }

  try {
    const secretKey = sodium.from_hex(import.meta.env.VITE_APP_SECRET);
    const encryptedMessage = Buffer.from(message, 'base64');
    const nonce = encryptedMessage.slice(0, sodium.crypto_secretbox_NONCEBYTES);
    const ciphertext = encryptedMessage.slice(
      sodium.crypto_secretbox_NONCEBYTES
    );

    const decryptedMessage = sodium.crypto_secretbox_open_easy(
      ciphertext,
      nonce,
      secretKey
    );

    const result = decryptedMessage
      ? sodium.to_string(decryptedMessage)
      : 'N/A';
    decryptionCache.set(message, result);
    return result;
  } catch (e) {
    console.error('Decryption error:', e, 'for message:', message);
    decryptionCache.set(message, 'N/A');
    return 'N/A';
  }
};

// Event dispatching for re-rendering
const triggerRerender = () => {
  document.dispatchEvent(new CustomEvent('decryption-updated'));
};

// Hook for listening to updates
export const useDecryptionUpdates = (callback: () => void) => {
  React.useEffect(() => {
    const handleUpdate = () => callback();
    document.addEventListener('decryption-updated', handleUpdate);
    return () =>
      document.removeEventListener('decryption-updated', handleUpdate);
  }, [callback]);
};
